import type { StatusMap } from "@vixio/ui";

export type InvoiceStatus = "draft" | "queued" | "processing" | "issued" | "rejected" | "cancel_requested" | "canceled";
export const INVOICE_STATUS: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  queued: { label: "na fila", tone: "info" },
  processing: { label: "processando", tone: "info" },
  issued: { label: "emitida", tone: "ok" },
  rejected: { label: "rejeitada", tone: "crit" },
  cancel_requested: { label: "cancelamento pedido", tone: "warn" },
  canceled: { label: "cancelada", tone: "neutral" },
};
export const FISCAL_PROVIDERS = { focus_nfe: "Focus NFe", nfe_io: "NFE.io", enotas: "eNotas", abrasf_direto: "ABRASF direto", nacional: "NFS-e Nacional", manual: "Manual" } as const;
export const AUTO_INVOICE = { off: "Desligada", on_receivable: "Ao criar o título", on_payment: "Ao receber" } as const;

export interface InvoiceView {
  id: string;
  issuer_id: string;
  person_id: string | null;
  status: InvoiceStatus;
  number: string | null;
  verification_code: string | null;
  taker_name: string | null;
  taker_type: "pf" | "pj" | null;
  description: string;
  amount_cents: number;
  deductions_cents: number;
  iss_aliquot_pct: number;
  iss_cents: number;
  iss_retained: boolean;
  retentions_total_cents: number;
  net_cents: number;
  competence: string;
  issued_at: string | null;
  canceled_at: string | null;
  origin_type: string | null;
  origin_id: string | null;
  receivable_id: string | null;
  pdf_url: string | null;
  xml_url: string | null;
  provider: string | null;
  environment: string | null;
  source: string;
  created_at: string;
}

export interface InvoiceFull extends InvoiceView {
  taker: { name?: string; doc?: string; doc_type?: string; email?: string } & Record<string, unknown>;
  retentions: Record<string, number | string[] | null>;
  service_code: string | null;
  lc116_item: string | null;
  rejection_reason: string | null;
  cancel_reason: string | null;
  last_error: string | null;
  attempts: number;
  rps_series: string | null;
  rps_number: number | null;
}

export interface IssuerConfig {
  id: string;
  issuer_id: string;
  provider: keyof typeof FISCAL_PROVIDERS;
  environment: "homologacao" | "producao";
  municipal_service_code: string | null;
  lc116_item: string | null;
  national_tax_code: string | null;
  cnae: string | null;
  iss_aliquot_pct: number;
  iss_retained_default: boolean;
  federal_withholding: boolean;
  inss_cession: boolean;
  simples_anexo: string | null;
  rps_series: string | null;
  next_rps_number: number;
  certificate_ref: string | null;
  provider_token_ref: string | null;
  webhook_secret_ref: string | null;
  auto_invoice: keyof typeof AUTO_INVOICE;
  auto_queue: boolean;
  default_description: string | null;
  active: boolean;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "fiscal.create_invoice": {
      args: {
        p_org: U; p_issuer: U; p_taker: Record<string, unknown>; p_description: string; p_amount_cents: number; p_competence?: string | null;
        p_person?: U | null; p_deductions_cents?: number; p_service_code?: string | null; p_iss_retained?: boolean | null; p_idempotency_key?: string | null;
      };
      returns: U;
    };
    "fiscal.queue_invoice": { args: { p_invoice: U }; returns: null };
    "fiscal.cancel_invoice": { args: { p_invoice: U; p_reason: string }; returns: string };
    "fiscal.record_manual_issue": { args: { p_invoice: U; p_number: string; p_verification_code?: string | null; p_issued_at?: string | null; p_pdf_url?: string | null }; returns: null };
    "fiscal.upsert_issuer_config": { args: { p_issuer: U; p_patch: Partial<IssuerConfig> }; returns: U };
  }
}
